/**
 * AtlasVROptimizerModule — VR-only render fixes + a Quest-class performance
 * profile, applied for the lifetime of an immersive-vr session and fully
 * restored on exit. Desktop behavior is untouched: every change is
 * edge-triggered on the session boundary and every touched value is
 * snapshotted first, so outside a session this module is inert.
 *
 * WHY EACH FIX (Quest 3 field report: dark ground, a sliver of sky, pitch-up
 * glitching — all three trace to the same render-state problems):
 *
 * 1. CLEAR THE XR FRAMEBUFFER. EngineRenderer sets `autoClear = false` once,
 *    globally; the desktop path compensates because post-processing's PassNode
 *    force-clears around the scene render, but the engine BYPASSES post while
 *    `xr.isPresenting` and issues a bare render — so the XR depth buffer
 *    accumulates from frame 1. Stale depth is exactly "can look down (new near
 *    fragments win), can't look back up (sky/horizon at far depth loses)", and
 *    the depth-less Hillaire background quad paints then everything behind
 *    stale depth vanishes → dark ground + a small surviving sky patch.
 *    Fix: `autoClear = true` while presenting (restored on exit), enforced by
 *    a Render/PreUpdate system in case any other code flips it mid-session.
 *
 * 2. VR DEPTH RANGE. The desktop camera runs near 0.05 / far 4000 — viable on
 *    WebGPU's [0,1] reversed depth, but immersive-vr forces the WebGL2 backend
 *    ([-1,1] NDC, no reversed-z) where that ratio (80,000:1) destroys depth
 *    precision at distance. VR clamps to near 0.1 / far 1500 (sky is a
 *    depth-less background node, so the far plane never clips it) and three
 *    propagates the clamp to session.updateRenderState({depthNear, depthFar}).
 *
 * 3. VR QUALITY PROFILE. A Quest 3 auto-detects the desktop 'high' tier
 *    (4×2048 CSM cascades, 400 m cull, full foliage — the measured triangle
 *    whale, ~64 M tris of trees — and an 8K night sky) against an 11-13 ms
 *    mobile GPU budget. While presenting we overlay a VR profile onto the LIVE
 *    activePreset() object (mutating it directly — deliberately NOT via the
 *    quality_tiers setters, which persist to localStorage; the VR profile must
 *    never leak into the player's saved desktop settings, even on a crash) and
 *    rebuild via PostModule.applySettings(), which re-emits
 *    `atlas:quality-changed` so Lighting (CSM teardown), Environment (foliage
 *    re-scatter), Culling, Streaming and Atmosphere all rescale. Shadows go
 *    OFF entirely in VR: beyond the GPU cost, CSMShadowNode fits cascades to a
 *    camera projection that three replaces with the stereo union each XR frame
 *    while `sunShadowGate().autoUpdate = false` keeps the maps stale — moving
 *    cascade cameras under frozen maps paints broad wrong shadow across the
 *    ground (the other half of "the ground is dark").
 *
 * Foveation 0.7 / framebufferScale 0.9 / 72 Hz are already applied by the
 * XRModule config in main.ts before setSession, per the engine contract.
 */

import type * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { Phase, Sub, makeSystem } from '../engine-compat.js';
import { activePreset, type TierPreset } from '../lib/quality_tiers.js';
import type { PostModule } from './PostModule.js';

/** VR camera depth range (see WHY #2). */
const VR_NEAR = 0.1;
const VR_FAR = 1500;

/**
 * The VR overlay applied onto the live TierPreset (see WHY #3). Field notes:
 * - shadowsEnabled false — CSM off (perf + the stale-cascade artifact).
 * - cloudsEnabled false  — clouds composite in post, which never runs in XR;
 *                          disabling also skips the march-prep CPU work.
 * - post: explicit fxaa:false — post is bypassed in XR; keep the config
 *   minimal so an accidental future XR-post path costs nothing.
 * - fogScale 0.6 — night fog (#161e36 @ 0.003) reads as "world goes dark past
 *   ~230 m" in-headset; thinner fog keeps the horizon legible.
 * - lightingBoost 1.15 — compensates the luminance bloom/clouds/haze carry on
 *   desktop tiers (same rationale as the potato tier, milder).
 * - foliage 0.3 / farCull 200 / settlements low-LOD @ 0.6 radius — the
 *   draw-item + triangle levers, targeting ~50-100 draw calls (Meta guidance).
 * - maxTextureRes 1024 — halves sampler bandwidth on newly-parsed assets.
 */
const VR_PROFILE: Partial<TierPreset> = {
  post: { fxaa: { enabled: false } },
  pixelRatio: 1,
  particleCap: 0,
  fogScale: 0.6,
  cloudsEnabled: false,
  shadowsEnabled: false,
  // 1.35 (was 1.15): the session screenshot analysis (2026-08-16, 29 real
  // headset frames) showed the night ground rendering PITCH black in-VR —
  // with clouds/shadows/bloom all off, the luminance they carry on desktop
  // is gone; match the potato tier's compensation so the night world stays
  // legible in-headset.
  lightingBoost: 1.35,
  settlementRadiusScale: 0.5,
  settlementForceLowLod: true,
  surfaceDetail: false,
  // 0 (was 0.3): foliage scatter + its per-frame influence updates are pure
  // cost on a mobile GPU/CPU; grass is invisible at VR pixel density anyway.
  foliageDensityScale: 0,
  lodDistanceScale: 1.0,
  // 160 (was 200): still past the 0.6-scaled night-fog legibility wall, one
  // more ring of draw items gone.
  farCull: 160,
  maxTextureRes: 1024,
  drawCallBudget: 2500,
  memoryBudgetMb: 512,
  prefetchRegions: 1,
};

export class AtlasVROptimizerModule implements IModule {
  readonly name = 'AtlasVROptimizer';

  private app!: App;
  /** Non-null exactly while a session's overrides are applied. */
  private saved: {
    autoClear: boolean;
    near: number;
    far: number;
    preset: TierPreset;
  } | null = null;
  /** Set when the session started before the avatar rig existed. */
  private cameraClampPending = false;

  init(app: App): void {
    this.app = app;
    const xrm = app.renderer.native.xr;
    xrm.addEventListener('sessionstart', () => this.apply());
    xrm.addEventListener('sessionend', () => this.restore());

    // Insurance: keep the XR framebuffer clearing even if other code touches
    // autoClear mid-session, and apply the camera clamp late when VR was
    // entered before the avatar rig existed. Runs before the engine's XR
    // systems (XRCameraOverride at -100) and costs two branches per frame.
    app.world.addSystem(
      makeSystem(
        'AtlasVRState',
        Phase.Render,
        -150,
        () => {
          if (!this.saved) return;
          const r = this.app.renderer.native;
          if (!r.autoClear) r.autoClear = true;
          if (this.cameraClampPending) this.clampCamera();
        },
        Sub.PreUpdate,
      ),
    );
  }

  // ─── Session boundary ─────────────────────────────────────────────────────────

  private apply(): void {
    if (this.saved) return; // double sessionstart guard
    const r = this.app.renderer.native;
    const live = activePreset();

    this.saved = {
      autoClear: r.autoClear,
      near: this.camera()?.near ?? VR_NEAR,
      far: this.camera()?.far ?? VR_FAR,
      // Deep-enough snapshot: `post` is the only nested object on TierPreset.
      preset: { ...live, post: { ...live.post } },
    };

    // #1 — clear the XR framebuffer every frame.
    r.autoClear = true;

    // #2 — WebGL2-safe depth range (deferred until the rig exists if needed).
    this.cameraClampPending = true;
    this.clampCamera();

    // #3 — VR quality profile onto the live settings; consumers rescale on
    // the quality-changed emit. No saveSettings() anywhere in this path.
    Object.assign(live, VR_PROFILE, { post: { ...VR_PROFILE.post } });
    this.reapply();
    bus.emit('atlas:status', { text: 'VR profile active (shadows off · short fog · reduced foliage)' });
  }

  private restore(): void {
    const saved = this.saved;
    if (!saved) return;
    this.saved = null;
    this.cameraClampPending = false;

    const r = this.app.renderer.native;
    r.autoClear = saved.autoClear;

    const cam = this.camera();
    if (cam) {
      cam.near = saved.near;
      cam.far = saved.far;
      cam.updateProjectionMatrix();
    }

    // Restore every preset field (Object.assign covers all keys VR touched,
    // because the snapshot is a full TierPreset), then rebuild.
    Object.assign(activePreset(), saved.preset, { post: { ...saved.preset.post } });
    this.reapply();
  }

  // ─── Helpers ──────────────────────────────────────────────────────────────────

  private camera(): THREE.PerspectiveCamera | null {
    const cam = atlas.localMesh?.children[0];
    return cam && (cam as THREE.PerspectiveCamera).isPerspectiveCamera
      ? (cam as THREE.PerspectiveCamera)
      : null;
  }

  private clampCamera(): void {
    const cam = this.camera();
    if (!cam) return; // rig not built yet — the per-frame system retries
    cam.near = VR_NEAR;
    cam.far = VR_FAR;
    cam.updateProjectionMatrix();
    this.cameraClampPending = false;
  }

  /** Rebuild post + fan out atlas:quality-changed / atlas:environment-updated. */
  private reapply(): void {
    const post = this.app.getModule('Post') as PostModule | undefined;
    if (post) {
      post.applySettings();
    } else {
      // Headless/verify environments without the Post module: fan out directly.
      bus.emit('atlas:quality-changed', {});
      bus.emit('atlas:environment-updated', {});
    }
  }

  dispose(): void {
    // Mid-session teardown must not leave VR overrides on the desktop state.
    this.restore();
  }
}
